import { Injectable } from '@angular/core';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES, AxwayTokenFetcherService } from '@ptl/xhr';
import { HttpHeaders } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { Email } from '../../producer/interfaces/email.interface';
import { StateStructure, resetStore } from '@ptl/state';
import { NgRedux } from '@ptl/angular-redux-store';
import { USER_ROLE, UserInfoStateStructure, LOGIN_INFO_REDUCER_NAME } from '@ptl/account-management';
import { AgentCubeDemographicInfo } from '../components/interfaces/agent-cube-demographic-info.interface';
import { PtlWindowLocationService } from '@ptl/global-components';
import { BrandingConfigurationService } from '@ptl/brand';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { environment } from 'src/environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SalesForceDemographicInfo } from '../components/interfaces/sales-force-demographic-info.interface';
import { WhbCrosswalk } from '../components/interfaces/member-lookup.interface';
import { API_ENDPOINTS } from 'src/app/shared/constants/api-endpoints.constant';

@Injectable({
  providedIn: 'root'
})

export class UserInfoService {
  /** AEM Saved Content */
  consumerLogoutRedirectUrl: string = '/home';
  producerLogoutRedirectUrl: string = '/login';

  /** This'll emit when logged in */
  isLoggedInBehaviorSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isLoggedIn$ = this.isLoggedInBehaviorSubject.asObservable();

  constructor(
    private httpClient: HTTPWrapperContext,
    private reducerUtilityService: ReducerUtilityService,
    private ngRedux: NgRedux<StateStructure>,
    private windowLocationService: PtlWindowLocationService,
    private brandService: BrandingConfigurationService,
    private commonUtility: CommonUtilityService,
    private snackBar: MatSnackBar,
    private axwayTokenFetcherService: AxwayTokenFetcherService
  ) {
  }

  getSecurityQues(): Promise<any> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      // url: '/assets/mock-data/security-questions.json',
      url: '/rbsmbl/x-services/user/securityQuestions',
      headers: new HttpHeaders({
      }),
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest(requestConfig);
  }

  submitPwdResetRequest(payload: any): Promise<any> {


    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
        ddToken: 'MSwzLDc=', // @TODO: this.encodeDdtoken(environment.initData.brand.ddToken)
      }),
      POST_OR_PUT_PAYLOAD: payload,
      withCredentials: true,
      url: `/rbsmbl/x-services/user/changePassword`
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.DATAPOWER_MIDDLEWARE);
    return this.httpClient.makeRequest(requestConfig);
  }

  /** Allow this to be called in order to identify user as logged in */
  emitLoginSuccess() {
    this.isLoggedInBehaviorSubject.next(true);
  }

  emitLoginFailure() {
    this.isLoggedInBehaviorSubject.next(false);
  }

  resetStore() {
    this.ngRedux.dispatch(resetStore());
    this.isLoggedInBehaviorSubject.next(false);
  }

  resetStoreAndRedirect(producerRedirectPage?: string, showUnauthorized?: boolean) {
    this.resetStore();

    if (this.commonUtility.isConsumerDomain()) {
      this.windowLocationService.goToUrl(this.consumerLogoutRedirectUrl);
    }
    else {
      this.redirectLoggedOutProducerUser(producerRedirectPage, showUnauthorized);
    }
  }

  getLoggedInUserRole(): USER_ROLE {
    const userData: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const roleAsString = (
      userData &&
      userData.basicUserInfo &&
      userData.basicUserInfo.userType &&
      userData.basicUserInfo.userType.toLowerCase()
    );

    return roleAsString as USER_ROLE;
  }

  setLogoutRedirectUrls(
    consumerLogoutRedirectUrl: string,
    producerLogoutRedirectUrl: string
  ) {
    this.consumerLogoutRedirectUrl = consumerLogoutRedirectUrl;
    this.producerLogoutRedirectUrl = producerLogoutRedirectUrl;
  }

  redirectLoggedOutProducerUser(producerRedirectPage: string, showUnauthorized: boolean) {
    const alertDuration = 1000;

    if (showUnauthorized) {
      const alertMessage = `You are not logged in. Sending you back to login`
      this.snackBar.open(alertMessage, 'UNAUTHORIZED', {
        duration: alertDuration,
        panelClass: 'negative-panel-class'
      });
    }

    setTimeout(() => {
      window.location.assign(producerRedirectPage);
    }, alertDuration);
  }

  sendReferralEmail(email: Email): Promise<any> {
    const env = this.commonUtility.getTestEnvironment();

    const payload = {
      baseMessage: {},
      payload: email
    };
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
        'x-hmhs-test-backend': `wasinter${env}.highmark.com`
      }),
      withCredentials: true,
      POST_OR_PUT_PAYLOAD: payload,
      url: this.axwayTokenFetcherService.buildApiUrl(API_ENDPOINTS.EMAIL_SERVICES.SEND_EMAIL_EXACTTARGET, {})
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS_V4);
    return this.httpClient.makeRequest(requestConfig);
  }

  getDemographicDataAgentCube(customerId: string): Promise<AgentCubeDemographicInfo> {
    const payload = {
      getCustomerInputField: {
        customer_IDField: customerId,
        returnIndividualsInformationField: 1,
        returnNotesField: false
      }
    };
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
      }),
      withCredentials: true,
      POST_OR_PUT_PAYLOAD: payload,
      url: `/ClientService/v3/ExtendedRest.svc/GetCustomer`
    });
    
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  getDemographicDataSalesForce(opportunityId: string): Promise<SalesForceDemographicInfo> {
    const clientSecret = environment.infrastructure.salesForce.client_secret;
    const clientId = environment.infrastructure.salesForce.client_id;
    const hmhsBackendHeader = environment.infrastructure.salesForce.hmhsBackendHeader;
    const payload = {id: opportunityId};
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
        'client_id': clientId,
        'client_secret': clientSecret,
        'source-api': 'ShopX',
        'x-hmhs-test-backend': hmhsBackendHeader
      }),
      withCredentials: true,
      POST_OR_PUT_PAYLOAD: payload,
      url: `/sfhc/v3/sales/account/opportunity`
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  doMedigapWHBCrosswalk(umi: string): Promise<WhbCrosswalk> {
    // MEDIGAP CROSSWALK FOR WHB
    const payload = {
      searchIdentifier: umi
    };

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/sxesvc/api/v1/whb/crosswalk',
      headers: new HttpHeaders({
      }),
      POST_OR_PUT_PAYLOAD: payload
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  memberLookup(umi: string, dob: string, isPlanChange: boolean, disableSnackbar?: boolean): Promise<any> {
    let payload: any;
    let requestHeaders = new HttpHeaders();

    if (isPlanChange) {
      payload = {
        internalSystems: 'MAPS',
        submitType: 'CHANGEPLAN_RETRIEVE',
        payload: {
          mapsRequestPayload: {
            TELESALESUSER: true,
            DOB: dob,
            UMI: umi
          }
        }
      };

      // ddToken needed only for plan change
      requestHeaders = requestHeaders.set('ddToken', this.brandService.getCurrentBrand().ddToken_encoded());
    }
    else {
      payload = {
        internalSystems: 'IPAT',
        submitType: 'CHANGEPLAN_RETRIEVE',
        payload: {
          ipatRequestPayload: {
            TELESALESUSER: true,
            DOB: dob,
            UMI: umi
          }
        }
      };
    }
   
    requestHeaders = requestHeaders.set('x-hmhs-test-backend', environment.external.engen.eBill.overrideEnv);

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      headers: requestHeaders,
      POST_OR_PUT_PAYLOAD: payload,
      url: `/cmcsnr/v3/applicationMethods/retrieveApp`
    });

    if (disableSnackbar) {
      this.httpClient.disableErrorSnackbar(requestConfig);
    }

    this.httpClient.setStrategy(XHR_STRATEGIES.POST_PASS_WAS);
    return this.httpClient.makeRequest(requestConfig);
  }
}
